import { SAVED_DASHBOARDS_STATE_KEY } from '@utils/constants';
import { setSaved } from '@store/tempDashboard/actions';
import { get, isEqual, set } from 'lodash';
import React, { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

type SavedDashboardMetaType = {
  id?: string;
  isSaved?: boolean;
  hasChanged?: boolean;
  isPublic?: boolean;
  creatorId?: number;
  name?: string;
  dashboardTypeId?: number;
};

export interface SavedDataInterface {
  meta: SavedDashboardMetaType;
  config: object;
  initialSavedData: any;
}

export type SavingStateObjectType = {
  meta?: SavedDashboardMetaType;
  config?: object;
  configPath?: string[];
  configValue?: any;
};

// This hook can accept the full or partial meta object
// and/or the full or partial config object.
// It also has the provision to accept a configPath as an array
// and a corresponding configValue for that path, to be able update some config item specifically
function useSaveSet({ pageKey, saveData }: { pageKey: string; saveData: SavingStateObjectType }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  useEffect(() => {
    try {
      const currentSaveData: SavedDataInterface = queryClient.getQueryData<any>([
        SAVED_DASHBOARDS_STATE_KEY,
        pageKey,
      ]);

      const { initialSavedData = {} } = currentSaveData;
      const id = get(currentSaveData, ['meta', 'id'], false);
      let hasChanged = get(currentSaveData, ['meta', 'hasChanged'], false);

      if (saveData.meta) {
        currentSaveData.meta = {
          ...currentSaveData.meta,
          ...saveData.meta,
        };
      }

      if (saveData.config) {
        currentSaveData.config = {
          ...currentSaveData.config,
          ...saveData.config,
        };
      }

      if (saveData.configPath && saveData.configValue) {
        set(currentSaveData.config, saveData.configPath, saveData.configValue);
      }

      if (!hasChanged) {
        hasChanged = !isEqual(initialSavedData.config, currentSaveData.config);
        if (hasChanged) {
          set(currentSaveData.meta, ['hasChanged'], hasChanged);
          dispatch(setSaved(id, currentSaveData.config, hasChanged));
        }
      }

      queryClient.setQueryData([SAVED_DASHBOARDS_STATE_KEY, pageKey], currentSaveData);
    } catch (e) {
      console.log('get/set save status', e);
    }
  }, [
    queryClient,
    pageKey,
    saveData.meta,
    saveData.config,
    saveData.configPath,
    saveData.configValue,
    dispatch,
  ]);
}

export default useSaveSet;
